import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { CreateModuleError, useCreateModuleMutation } from '@entities/Module';
import { buildComposerState, paths } from '@shared/config';
import { cn } from '@shared/lib';
import { LANGUAGE_LABELS, type Language, type Level } from '@shared/types';
import { PlainField } from '@shared/ui';
import { useModuleCreatorStore } from '../model/useModuleCreatorStore';
import { LanguageSelect } from './LanguageSelect';

const LANGUAGES: Language[] = ['en', 'ru', 'de', 'fr', 'es', 'it'];
// Пока поддерживается только пара English → Русский. Остальные языки видны
// в списке, но заблокированы («скоро») — расширять одновременно с бэком.
const AVAILABLE_SOURCE: Language[] = ['en'];
const AVAILABLE_TARGET: Language[] = ['ru'];
const NAME_MAX = 48;
// Уровень пока скрыт в UI — под капотом всегда создаём модуль на B1.
const DEFAULT_LEVEL: Level = 'B1';

export function ModuleCreatorModal() {
  const open = useModuleCreatorStore((s) => s.open);
  const close = useModuleCreatorStore((s) => s.close);
  const navigate = useNavigate();
  const createMutation = useCreateModuleMutation();
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [sourceLang, setSourceLang] = useState<Language>('en');
  const [targetLang, setTargetLang] = useState<Language>('ru');
  const [focusField, setFocusField] = useState<'name' | 'desc' | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const resetMutationRef = useRef(createMutation.reset);
  resetMutationRef.current = createMutation.reset;

  // Сброс формы и автофокус на названии при открытии.
  useEffect(() => {
    if (!open) return;
    setName('');
    setDesc('');
    setSourceLang('en');
    setTargetLang('ru');
    setFocusField(null);
    resetMutationRef.current();
    const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.clearTimeout(timer);
  }, [open]);

  // Esc для закрытия + focus-trap внутри диалога (как в shared Modal).
  useEffect(() => {
    if (!open) return;
    lastFocusedRef.current = document.activeElement as HTMLElement | null;

    const getFocusable = (): HTMLElement[] => {
      const dialog = dialogRef.current;
      if (!dialog) return [];
      return Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),textarea,input:not([disabled]),select,[tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null);
    };

    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = getFocusable();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      lastFocusedRef.current?.focus?.();
    };
  }, [open, close]);

  // Source и target должны быть разными — иначе нет смысла переводить.
  const sameLanguage = sourceLang === targetLang;
  const valid = name.trim().length > 0 && !sameLanguage;

  const error = createMutation.error instanceof CreateModuleError ? createMutation.error : null;
  const errorCode = error?.code ?? null;
  // Модерация на бэке: поле с 18+, жестокостью или ненавистью не сохраняем.
  const restricted = errorCode === 'RESTRICTED' ? (error?.fields ?? []) : [];
  const nameFieldError =
    errorCode === 'DUPLICATE_NAME'
      ? 'Модуль с таким названием уже есть'
      : restricted.includes('name')
        ? 'Название не прошло проверку на 18+, жестокость и ненависть — перепиши его'
        : null;
  const descFieldError = restricted.includes('description')
    ? 'Описание не прошло проверку на 18+, жестокость и ненависть — перепиши его'
    : null;
  // Причину говорим прямо: на общий «Попробуй ещё раз» пользователь жал 11 раз подряд
  // (402 в логах 2026-10-15), повтор тут не поможет.
  const bannerError: ReactNode =
    errorCode === 'NETWORK'
      ? 'Нет соединения. Проверь сеть и попробуй ещё раз.'
      : errorCode === 'RESTRICTED'
        ? restricted.length === 0
          ? 'Название или описание не прошло проверку на 18+, жестокость и ненависть.'
          : null
        : errorCode === 'EMAIL_NOT_VERIFIED'
            ? 'Сначала подтверди почту — ссылка в письме после регистрации. Потом можно создавать модули.'
            : errorCode === 'RATE_LIMIT'
              ? 'Слишком много попыток подряд. Подожди минуту и попробуй снова.'
              : errorCode === 'SERVER'
                ? 'Сервер сейчас не отвечает. Попробуй ещё раз через минуту.'
                : errorCode && errorCode !== 'DUPLICATE_NAME'
                  ? 'Не удалось создать модуль. Попробуй ещё раз.'
                  : null;

  const submit = () => {
    if (!valid || createMutation.isPending) return;
    createMutation.mutate(
      { name, desc, cefrLevel: DEFAULT_LEVEL, sourceLang, targetLang },
      {
        onSuccess: (created) => {
          const onCreated = useModuleCreatorStore.getState().onCreated;
          close();
          // Открыли из выбора модуля — выбираем созданный там же. Иначе свежий модуль пуст — ведём в строку
          // создания на «Словах и модулях» с ним же (страницы «Из словаря» больше нет, 2026-10-21).
          if (onCreated) onCreated(created);
          else navigate(paths.words, { state: buildComposerState(created.id) });
        },
      },
    );
  };

  const onNameChange = (value: string) => {
    setName(value);
    if (errorCode === 'DUPLICATE_NAME' || errorCode === 'RESTRICTED') createMutation.reset();
  };

  const onDescChange = (value: string) => {
    setDesc(value);
    if (errorCode === 'RESTRICTED') createMutation.reset();
  };

  const onDialogKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
  };

  if (!open) return null;

  const fieldFocus = (field: 'name' | 'desc') =>
    focusField === field ? 'border-accent/55 shadow-[0_0_0_3px_rgba(243,106,28,0.12)]' : 'border-white/[0.09]';

  // Телефон (до 640 px) — лист снизу во всю ширину, компактнее: без выбора языков (доступна только пара
  // English → Русский, она видна строкой «Карточки: …»), подсказка про описание — одной строкой,
  // крестик вместо «ESC» (решение владельца 2026-10-21: на узком экране модалка почти не помещалась).
  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-end justify-center overflow-hidden sm:items-center sm:p-6">
      {/* Затемнение + размытие страницы под модалкой */}
      <div
        aria-hidden
        onClick={close}
        className="absolute inset-0 animate-cm-backdrop-in bg-[rgba(8,7,6,0.55)] backdrop-blur-[3px]"
      />
      {/* Мягкая «аврора» — медленно дрейфующее янтарное свечение */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[8%] -ml-[340px] hidden h-[520px] w-[680px] animate-cm-aurora bg-[radial-gradient(circle,rgba(243,106,28,0.16),transparent_65%)] blur-[28px] sm:block"
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Создать модуль"
        tabIndex={-1}
        onKeyDown={onDialogKeyDown}
        className={cn(
          'relative max-h-[calc(100dvh-12px)] w-full max-w-[560px] animate-cm-dialog-in overflow-y-auto rounded-t-[20px] border border-b-0 border-white/[0.08] outline-none',
          'sm:max-h-none sm:overflow-visible sm:rounded-[20px] sm:border-b',
          'bg-gradient-to-b from-[#16140f] to-[#121110]',
          'shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_40px_90px_-30px_rgba(0,0,0,0.8),0_0_0_1px_rgba(243,106,28,0.05)]',
        )}
      >
        {/* Верхний акцент-хайрлайн */}
        <div className="h-0.5 rounded-t-[20px] bg-gradient-to-r from-transparent via-accent/50 to-transparent" />

        {/* Шапка */}
        <div className="px-5 pt-4 sm:px-[30px] sm:pt-[26px]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-[#9c7a5c]">Новый модуль</div>
              <h1 className="mt-1.5 font-serif text-[24px] font-normal leading-[1.05] tracking-[0.01em] text-[#f2efe8] sm:mt-2 sm:text-[30px]">
                Как назовём?
              </h1>
            </div>
            <button
              type="button"
              onClick={close}
              className="hidden rounded-lg border border-white/[0.09] bg-white/[0.03] px-[11px] py-[5px] text-[12px] font-semibold tracking-[0.08em] text-[#8a877f] transition-colors hover:bg-white/[0.07] hover:text-[#cfcdc6] sm:block"
            >
              ESC
            </button>
            <button
              type="button"
              onClick={close}
              aria-label="Закрыть"
              className="-mr-2.5 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[18px] text-[#8a877f] transition-colors hover:text-[#cfcdc6] sm:hidden"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Тело */}
        <div className="flex flex-col gap-4 px-5 pb-1 pt-4 sm:gap-5 sm:px-[30px] sm:pt-[22px]">
          {/* Название */}
          <div>
            <label className="mb-[9px] block text-[12px] font-semibold uppercase tracking-[0.14em] text-[#7e7b73]">
              Название
            </label>
            <div className="relative">
              <PlainField
                ref={inputRef}
                value={name}
                placeholder="Например, Travel essentials"
                ariaLabel="Название"
                onChange={(v) => onNameChange(v.slice(0, NAME_MAX))}
                onFocus={() => setFocusField('name')}
                onBlur={() => setFocusField(null)}
                onKeyDown={(e) => {
                  // Только обычный Enter — Ctrl/⌘+Enter обрабатывает onDialogKeyDown,
                  // иначе submit() вызвался бы дважды (двойная мутация).
                  if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
                className={cn(
                  'w-full rounded-[11px] border bg-[#1b1813] py-[13px] pl-[15px] pr-14 text-[15px] text-[#f0ede6] outline-none transition-[border-color,box-shadow] placeholder:text-[#5b594f]',
                  nameFieldError ? 'border-danger' : fieldFocus('name'),
                )}
              />
              {/* Счётчик — только когда есть текст: на 320 px он налезал на подсказку пустого поля. */}
              {name.length > 0 && (
                <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 font-mono text-[12px] text-[#5f5d56]">
                  {name.length}/{NAME_MAX}
                </span>
              )}
            </div>
            {nameFieldError && <div className="mt-2 text-[12px] tracking-[0.02em] text-danger">{nameFieldError}</div>}
          </div>

          {/* Описание */}
          <div>
            <div className="mb-[9px] flex items-baseline justify-between gap-3">
              <label className="block text-[12px] font-semibold uppercase tracking-[0.14em] text-[#7e7b73]">
                Описание
              </label>
              <span className="text-[12px] text-[#605e57]">необязательно</span>
            </div>
            <textarea
              value={desc}
              rows={2}
              placeholder="Например: поездка за границу — аэропорт, отель, кафе"
              onChange={(e) => onDescChange(e.target.value)}
              onFocus={() => setFocusField('desc')}
              onBlur={() => setFocusField(null)}
              className={cn(
                'w-full resize-none rounded-[11px] border bg-[#1b1813] px-[15px] py-[13px] text-[15px] leading-[1.5] text-[#f0ede6] outline-none transition-[border-color,box-shadow] placeholder:text-[#5b594f]',
                descFieldError ? 'border-danger' : fieldFocus('desc'),
              )}
            />
            {descFieldError && <div className="mt-2 text-[12px] tracking-[0.02em] text-danger">{descFieldError}</div>}
            {/* Плашка, а не серая подпись: без неё описание пропускали, не зная, зачем оно. На телефоне —
                одной строкой, чтобы лист помещался. */}
            <div className="mt-2.5 hidden items-start gap-2.5 rounded-[11px] border border-accent/25 bg-accent/[0.08] px-3.5 py-[11px] text-[14px] leading-[1.5] text-[#cdbfb2] sm:flex">
              <span aria-hidden className="text-[14px] leading-[1.45] text-accent">
                ✦
              </span>
              <span>
                <span className="font-semibold text-[#f0ede6]">ИИ учтёт описание:</span> примеры в карточках и
                упражнения будут на тему модуля.
              </span>
            </div>
            <p className="mt-2 flex items-start gap-2 text-[13px] leading-[1.45] text-[#9a8d80] sm:hidden">
              <span aria-hidden className="text-accent">
                ✦
              </span>
              ИИ возьмёт примеры и упражнения на тему описания.
            </p>
          </div>

          {/* Языки: на телефоне выбора нет — доступна одна пара, она видна строкой ниже. */}
          <div className="hidden grid-cols-2 items-start gap-4 sm:grid">
            <LanguageSelect
              label="Изучаю"
              value={sourceLang}
              onChange={setSourceLang}
              options={LANGUAGES}
              available={AVAILABLE_SOURCE}
              tone="accent"
            />
            <LanguageSelect
              label="Перевод"
              value={targetLang}
              onChange={setTargetLang}
              options={LANGUAGES}
              available={AVAILABLE_TARGET}
              tone="neutral"
            />
          </div>

          {/* Направление перевода / предупреждение о совпадении языков */}
          {sameLanguage ? (
            <div className="-mt-1.5 text-[14px] tracking-[0.02em] text-warn">
              Изучаемый язык и язык перевода должны быть разными.
            </div>
          ) : (
            <div className="flex items-center gap-2 text-[14px] text-[#75726b] sm:-mt-1.5">
              <span className="text-[#9c7a5c]">↳</span>
              Карточки: <span className="text-[#cbc8c1]">{LANGUAGE_LABELS[sourceLang]}</span> →{' '}
              <span className="text-[#cbc8c1]">{LANGUAGE_LABELS[targetLang]}</span>
            </div>
          )}

          {bannerError && <div className="text-[12px] tracking-[0.02em] text-danger">{bannerError}</div>}
        </div>

        {/* Футер */}
        {/* На телефоне футер прилипает к низу листа: с открытой клавиатурой лист прокручивается, а кнопка видна. */}
        <div className="sticky bottom-0 mt-1.5 flex items-center justify-end gap-3 border-t border-white/[0.05] bg-[#121110] px-5 pb-[max(16px,env(safe-area-inset-bottom))] pt-4 sm:static sm:bg-transparent sm:px-[30px] sm:pb-[26px] sm:pt-[22px]">
          <button
            type="button"
            onClick={close}
            className="rounded-[11px] px-4 py-[11px] text-[14px] font-medium text-[#9a978f] transition-colors hover:text-[#ece9e3] sm:px-5"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!valid || createMutation.isPending}
            className={cn(
              'inline-flex min-h-11 flex-1 items-center justify-center gap-2.5 whitespace-nowrap rounded-[11px] px-[22px] py-[11px] text-[14px] font-semibold sm:flex-none',
              'transition-[transform,box-shadow,background-color,opacity] duration-150',
              valid && !createMutation.isPending
                ? 'bg-accent text-[#1a1109] shadow-[0_8px_22px_-8px_rgba(243,106,28,0.6)] hover:-translate-y-px'
                : 'cursor-not-allowed bg-accent/[0.18] text-[#8a6a4e] opacity-70',
            )}
          >
            <span className="text-[14px] leading-none">✦</span>
            {createMutation.isPending ? 'Создаём…' : 'Создать модуль'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
